import { cn, formatNumber, preventDefault } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CircleAlert, ClockAlert, Sunset } from "lucide-react";

const DEFAULT_THRESHOLD = BigInt(30000 * 1e6);

interface StoplightIndicatorLabelProps {
  value: bigint;
  online?: boolean;
  eligible?: boolean;
  threshold?: bigint;
  sunset?: boolean;
}

interface StoplightIndicatorProps extends StoplightIndicatorLabelProps {
  name?: string;
  iconSize?: number;
  className?: string;
  children?: React.ReactNode;
}

// TODO check online status as well, ie is not enough

export function StoplightLabel({
  online,
  eligible,
  value,
  sunset,
  threshold = DEFAULT_THRESHOLD,
}: StoplightIndicatorLabelProps) {
  const isOverThreshold = value >= threshold;
  return sunset ? (
    <>Sunset</>
  ) : !isOverThreshold ? (
    <>Funding</>
  ) : eligible && online ? (
    <>Active</>
  ) : (
    <>Waiting</>
  );
}

export function StoplightIndicator({
  value,
  name = "dualSTAKE",
  online,
  eligible,
  iconSize = 24,
  className,
  threshold = DEFAULT_THRESHOLD,
  sunset,
  children,
}: StoplightIndicatorProps) {
  const isOverThreshold = value >= threshold;
  const tooltipBackground = !isOverThreshold
    ? "bg-orange-600 dark:bg-orange-500"
    : eligible && online
    ? "bg-green-600 dark:bg-green-500"
    : "bg-red-600";
  const bgColor = !isOverThreshold
    ? "bg-orange-700"
    : eligible && online
    ? "bg-green-500"
    : "bg-red-500";
  const fgColor = !isOverThreshold
    ? "text-orange-500"
    : eligible && online
    ? "text-green-500"
    : "text-red-500 dark:text-red-500";
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger
          className="flex items-center cursor-help"
          onClick={preventDefault}
        >
          {children}
          {sunset ? (
            <div className={cn(`flex p-1 gap-2`, className)}>
              <Sunset className={fgColor} size={iconSize} />
            </div>
          ) : isOverThreshold && eligible && online ? (
            <div className={cn(`flex p-2 py-1 gap-2`, className)}>
              <div
                className={cn("w-3 h-3 rounded-full animate-pulse", bgColor)}
              />
            </div>
          ) : (
            <div className={cn(`flex p-1 gap-2 animate-pulse`, className)}>
              {!isOverThreshold ? (
                <ClockAlert className={fgColor} size={iconSize} />
              ) : (
                <CircleAlert className={fgColor} size={iconSize} />
              )}
            </div>
          )}
        </TooltipTrigger>
        <TooltipContent
          className={`${tooltipBackground} pointer-events-none flex flex-col gap-2`}
        >
          {sunset ? (
            <>
              <p className="font-bold">Status: Sunset</p>
              <p>
                This dualSTAKE has been deprecated due to lack of community
                interest
              </p>
            </>
          ) : !isOverThreshold ? (
            <>
              <p className="font-bold">Status: Funding</p>
              <p>Minimum stake for rewards: 30,000 ALGO</p>
              <p>
                Additional stake required: {formatNumber(threshold - value)}{" "}
                ALGO
              </p>
            </>
          ) : online && eligible ? (
            <>
              <p className="font-bold">Status: Active</p>
              <p>
                Minimum of 30,000 ALGO staked is met.
                <br />
                Node status is active.
              </p>
            </>
          ) : (
            <>
              <p className="font-bold">Status: Waiting</p>
              <p>
                {name} consensus status is {online ? "active" : "inactive"}.
                <br />
                Waiting for node runner.
                <br />
                <br />
                <u>Note: this status may take up to 15 minutes to update.</u>
              </p>
            </>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
